import useFetchData from 'app/hooks/useFetchData';
import {
  getHotelDetailDataApi,
  getCancelPolicyDataApi,
  getFlightDetailApi,
  getInfoOrderHolidayApi,
} from 'app/services/holidayApi';
import _ from 'lodash';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { IHolidayOrderDetail } from '../types';
import moment from 'moment';

interface UseOrderDetailProps {
  shouldValidate?: boolean;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onFinally?: () => void;
}
enum CabinClass {
  BUSINESS = 'Business',
  ECONOMY = 'Economy',
}

/**
 * Flat lookup table (O(1))
 * Không loop
 * Không includes
 * Không nested ternary
 */
const BOOKING_CLASS_LOOKUP = Object.freeze({
  // Business
  J: CabinClass.BUSINESS,
  C: CabinClass.BUSINESS,
  I: CabinClass.BUSINESS,
  // Economy
  Y: CabinClass.ECONOMY,
  W: CabinClass.ECONOMY,
  V: CabinClass.ECONOMY,
  B: CabinClass.ECONOMY,
  H: CabinClass.ECONOMY,
  K: CabinClass.ECONOMY,
  L: CabinClass.ECONOMY,
  M: CabinClass.ECONOMY,
  N: CabinClass.ECONOMY,
  Q: CabinClass.ECONOMY,
  T: CabinClass.ECONOMY,
  O: CabinClass.ECONOMY,
  R: CabinClass.ECONOMY,
  U: CabinClass.ECONOMY,
} as const);

type BookingCode = keyof typeof BOOKING_CLASS_LOOKUP;
export const useHolidayOrderDetail = ({
  shouldValidate = true,
  onSuccess,
  onError,
  onFinally,
}: UseOrderDetailProps = {}) => {
  const history = useHistory();
  const [data, setData] = useState<IHolidayOrderDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const getCabinClass = (bookingClass?: string): CabinClass | '' => {
    if (!bookingClass) return '';
    const code = bookingClass.trim().toUpperCase();
    return BOOKING_CLASS_LOOKUP[code as BookingCode] ?? '';
  };
  const mergeFlightData = (a: any[], b: any[]) => {
    return a.map(itemA => {
      const matched = b.find(
        itemB =>
          String(itemB.flightNumber) === String(itemA.flightNumber) &&
          itemB.departure === itemA.departure &&
          itemB.arrival === itemA.arrival,
      );
      return {
        ...itemA,
        carrier: matched?.carrier,
        carrierName: matched?.carrierName,
        duration: matched?.duration,
        departureInfo: matched?.departureInfo,
        arrivalInfo: matched?.arrivalInfo,
        aircraftName: matched?.aircraftInfo?.name,
        fareBasis: matched?.fareBasis,
        cabinClass: getCabinClass(matched?.bookingClass),
      };
    });
  };
  const mergeHotelData = (hotels: any[], hotelDetail: any) => {
    return hotels.map(hotel => {
      const isMatched = String(hotel.hotelId) === String(hotelDetail?.info?.id);
      return {
        ...hotel,
        ...(isMatched && {
          detail: hotelDetail,
        }),
      };
    });
  };
  const mergeHotelPolicyData = (hotels: any[], policies: any[]) => {
    return hotels.map(hotel => {
      if (!hotel?.detail) {
        return hotel;
      }
      const policiesByRatePlan = _.groupBy(policies, item =>
        String(item.ratePlanId),
      );
      return {
        ...hotel,
        detail: {
          ...hotel.detail,
          ratePlans: hotel.detail.ratePlans.map((ratePlan: any) => ({
            ...ratePlan,
            cancellationPolicies: policiesByRatePlan[String(ratePlan.id)] || [],
          })),
        },
      };
    });
  };
  const { onFetchData } = useFetchData({
    actionApi: getInfoOrderHolidayApi,
    IIFE: false,
    onBefore: () => setIsLoading(true),
    onSuccess: async response => {
      try {
        if (
          shouldValidate &&
          (response?.paymentStatus === 'success' ||
            response?.orderState?.value === 'success' ||
            response?.orderState?.value === 'cancelled')
        ) {
          history.push('/page-not-found');
          return;
        }
        const mergedData = {
          ...response,
          passengers: [],
          contact: undefined,
        };
        const flightsData = response?.orderItems?.flights;
        const bookingId = flightsData?.[0]?.bookingId;
        const dataHotels = response?.orderItems?.hotels || [];
        if (bookingId) {
          try {
            const detailBooking = await getFlightDetailApi({
              bookingId,
            });
            if (
              !_.isEmpty(detailBooking?.data?.listItinerary) &&
              !_.isEmpty(flightsData)
            ) {
              const flightTemp = mergeFlightData(
                flightsData,
                detailBooking?.data?.listItinerary,
              );
              mergedData.orderItems.flights = flightTemp;
            }
            if (!_.isEmpty(detailBooking?.data?.listPassenger)) {
              mergedData.passengers = detailBooking?.data?.listPassenger.map(
                (p: any, index: number) => ({
                  id: p.id || index + 1,
                  type: p.type || 'ADULT',
                  passengerType: p.type || 'ADULT',
                  title: p.title || '',
                  firstName: p.firstName || '',
                  lastName: p.lastName || '',
                  dateOfBirth: p.dateOfBirth || '',
                  gender: '',
                  documentType: '',
                  documentNumber: '',
                  document: p.document || undefined,
                  parentId: p.parentId || undefined,
                  paxId: p.paxId || undefined,
                  listTicket: p.listTicket || [],
                }),
              );
            }
            if (!_.isEmpty(detailBooking?.data?.contactInfo)) {
              mergedData.contact = {
                fullName: detailBooking.data.contactInfo?.fullName ?? '',
                email: detailBooking.data.contactInfo?.email ?? '',
                phone: detailBooking.data.contactInfo?.phoneNumber ?? '',
              };
            }
          } catch (err) {}
        }
        if (!_.isEmpty(dataHotels)) {
          const firstHotel = dataHotels?.[0];
          const paramsHotel = {
            hotelId: firstHotel.hotelId,
            roomTypeId: firstHotel.roomTypeId,
          };
          const paramPolicy = {
            hotelId: firstHotel.hotelId,
            checkInDate: moment(firstHotel.checkInDate).format('YYYY-MM-DD'),
            ratePlanId: firstHotel.ratePlanId,
          };
          try {
            const hotelByRoomType = await getHotelDetailDataApi(paramsHotel);
            if (!_.isEmpty(hotelByRoomType?.data)) {
              const hotelTemp = mergeHotelData(
                dataHotels,
                hotelByRoomType?.data,
              );
              mergedData.orderItems.hotels = hotelTemp;
            }
          } catch (err) {}
          try {
            const hotelPolicy = await getCancelPolicyDataApi(paramPolicy);
            if (!_.isEmpty(hotelPolicy?.data)) {
              const hotelTemp = mergeHotelPolicyData(
                mergedData.orderItems.hotels,
                hotelPolicy.data,
              );
              mergedData.orderItems.hotels = hotelTemp;
            }
          } catch (err) {}
        }
        setData(mergedData);
        onSuccess?.(mergedData);
      } finally {
        setIsLoading(false);
        onFinally?.();
      }
    },
    onError: error => {
      if (String(error?.response?.data?.code) === '403') {
        history.push('/page-not-found');
      }
      setIsLoading(false);
      onError?.(error);
      onFinally?.();
    },
  });

  return {
    data,
    isLoading,
    onFetchOrderDetail: onFetchData,
  };
};
